import { GetFileFromDirectoryStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import {
  DataInput,
  DatasetType,
  FileSelectionMode,
  FileCandidateScope,
  InputSource,
} from '@/types/automationFields.ts';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import { Stack } from '@mantine/core';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import * as React from 'react';
import {
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import { STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import { useEditingVersionSnapshot } from '@/context/EditVersionContext/useEditVersionContext.ts';
import JsonSchemaRefSelect from '@/pages/Editor/components/SharedFields/JsonSchemaRefSelect.tsx';
import { schemaSupportsOutputType } from '@/pages/Editor/components/StepSidebar/StepForms/TransformDataStep/transformDataOutputSchemaUtils.ts';

type props = {
  step: GetFileFromDirectoryStep;
};

type Fields = {
  file_to_get_source: InputSource;
  file_to_get_data_input: DataInput;
  file_to_get_description: string;
  candidate_scope?: FileCandidateScope;
  selection_mode: FileSelectionMode;
  selection_value_source: InputSource;
  selection_value: string;
  selection_value_data_input: DataInput;
  selected_output_contract?: GetFileFromDirectoryStep['selected_output_contract'];
};

const FILE_SELECTION_MODE_OPTIONS = [
  { value: FileSelectionMode.NaturalLanguage, label: 'Natural language description' },
  { value: FileSelectionMode.DeterministicRelativePath, label: 'Exact relative path' },
  { value: FileSelectionMode.DeterministicFileId, label: 'Exact file id' },
  { value: FileSelectionMode.DeterministicSourceName, label: 'Exact source or sheet name' },
];

const getSelectionValueLabel = (selectionMode: FileSelectionMode) => {
  if (selectionMode === FileSelectionMode.DeterministicFileId) {
    return 'File id';
  }
  if (selectionMode === FileSelectionMode.DeterministicSourceName) {
    return 'Source or sheet name';
  }
  return 'Relative path';
};

const getSelectionValuePlaceholder = (selectionMode: FileSelectionMode) => {
  if (selectionMode === FileSelectionMode.DeterministicFileId) {
    return 'Dataset file id';
  }
  if (selectionMode === FileSelectionMode.DeterministicSourceName) {
    return 'ProductionSalesBoardResults';
  }
  return 'folder/report.pdf';
};

const GetFileFromDirectoryFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });
  const editingVersion = useEditingVersionSnapshot();

  const emptyLogicDescription = useStepSpecificError(step, StepErrorType.EmptyDescription);
  const preferDeterministicSelection = useStepSpecificError(
    step,
    StepErrorType.PreferDeterministicSelection,
  );

  const invalidLogicDataInput = useStepSpecificError(step, StepErrorType.InvalidLogicDataInput);

  const selectionMode = fields.selection_mode ?? FileSelectionMode.NaturalLanguage;
  const isNaturalLanguageMode = selectionMode === FileSelectionMode.NaturalLanguage;
  const selectionValueLabel = getSelectionValueLabel(selectionMode);
  const selectedOutputContract = fields.selected_output_contract ?? null;

  return (
    <Stack w={'100%'}>
      <StyledSelect
        label="Files to consider"
        description="Choose whole retained files to select a workbook or archive. Choose parsed contents to select its sheets or extracted files."
        data={[
          { value: FileCandidateScope.ParsedLeaves, label: 'Parsed contents (default)' },
          { value: FileCandidateScope.RetainedFiles, label: 'Whole retained files' },
        ]}
        value={fields.candidate_scope ?? FileCandidateScope.ParsedLeaves}
        onChange={(value) => onChangeNoDelay({ candidate_scope: value as FileCandidateScope })}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Selection Mode"
        data={FILE_SELECTION_MODE_OPTIONS}
        value={selectionMode}
        onChange={(value) => onChangeNoDelay({ selection_mode: value as FileSelectionMode })}
        required={true}
        allowDeselect={false}
        clearable={false}
      />

      {isNaturalLanguageMode ? (
        <>
          <InputSourceSelectField
            label="File To Get Source"
            value={fields.file_to_get_source}
            onChange={(value) => onChangeNoDelay({ file_to_get_source: value })}
          />
          {inputSourceAcceptsUserSpecified(fields.file_to_get_source) && (
            <Stack>
              <StyledTextArea
                label="Description of file to retrieve from directory"
                value={fields.file_to_get_description}
                placeholder="File description"
                onChange={(event) => {
                  onChange({ file_to_get_description: event.currentTarget.value });
                }}
                required={true}
                autosize={true}
                minRows={2}
                error={emptyLogicDescription ?? preferDeterministicSelection}
                maxLength={STEP_LOGIC_MAX_LENGTH}
              />
            </Stack>
          )}
          {inputSourceAcceptsDataInput(fields.file_to_get_source) && (
            <SingleDataInputSelectorInner
              step={step}
              label={'File To Get Data Input'}
              dataInputError={invalidLogicDataInput}
              dataInput={fields.file_to_get_data_input}
              onChange={(dataInput) => onChangeNoDelay({ file_to_get_data_input: dataInput })}
            />
          )}
        </>
      ) : (
        <>
          <InputSourceSelectField
            label={`${selectionValueLabel} Source`}
            value={fields.selection_value_source}
            onChange={(value) => onChangeNoDelay({ selection_value_source: value })}
            allowBoth={false}
          />
          {inputSourceAcceptsUserSpecified(fields.selection_value_source) && (
            <StyledTextInput
              label={selectionValueLabel}
              value={fields.selection_value}
              placeholder={getSelectionValuePlaceholder(selectionMode)}
              onChange={(event) => {
                onChange({ selection_value: event.currentTarget.value });
              }}
              required={true}
              error={emptyLogicDescription}
              maxLength={STEP_LOGIC_MAX_LENGTH}
            />
          )}
          {inputSourceAcceptsDataInput(fields.selection_value_source) && (
            <SingleDataInputSelectorInner
              step={step}
              label={`${selectionValueLabel} Data Input`}
              dataInputError={invalidLogicDataInput}
              dataInput={fields.selection_value_data_input}
              onChange={(dataInput) => onChangeNoDelay({ selection_value_data_input: dataInput })}
            />
          )}
        </>
      )}
      <StyledSelect
        label="Expected Selected Dataset Type"
        description="Optional. When set, the selected child must match this type before the step can succeed."
        placeholder="Unknown / accept any selected type"
        data={Object.values(DatasetType)
          .filter((type) => type !== DatasetType.Unknown)
          .map((type) => ({ value: type, label: type }))}
        value={selectedOutputContract?.dataset_type ?? null}
        onChange={(value) =>
          onChangeNoDelay({
            selected_output_contract: value
              ? {
                  dataset_type: value as DatasetType,
                  schema_ref: null,
                }
              : null,
          })
        }
        clearable
      />
      {selectedOutputContract &&
        [DatasetType.Json, DatasetType.Dataframe, DatasetType.File].includes(
          selectedOutputContract.dataset_type,
        ) && (
          <JsonSchemaRefSelect
            label="Expected Selected Schema"
            description="Optional automation-owned schema enforced after selection."
            schemas={(editingVersion?.schemas ?? []).filter((schema) =>
              schemaSupportsOutputType(schema, selectedOutputContract.dataset_type),
            )}
            value={selectedOutputContract.schema_ref}
            onChange={(schemaRef) =>
              onChangeNoDelay({
                selected_output_contract: {
                  ...selectedOutputContract,
                  schema_ref: schemaRef,
                },
              })
            }
          />
        )}
    </Stack>
  );
};

export default GetFileFromDirectoryFields;
